import type {
  BriefRunRecord,
  NoteDraftInput,
  NoteDraftSuggestion,
  PithyNote,
  RiskType,
  SignalStrength,
  SuggestedAction,
  WeeklyBrief,
} from "./types.ts";

const SIGNAL_RANK: Record<SignalStrength, number> = {
  low: 1,
  medium: 2,
  high: 3,
};

const ACTION_RANK: Record<SuggestedAction, number> = {
  monitor: 1,
  prepare: 2,
  act: 3,
};

export function buildWeeklyBrief(notes: PithyNote[], now = new Date()): WeeklyBrief {
  const sorted = [...notes].sort((left, right) => scoreNote(right) - scoreNote(left));
  const topSignals = sorted.slice(0, 3);
  const watchlist = sorted.slice(3, 7);
  const actionCounts = countByAction(notes);
  const signalCounts = countByStrength(notes);
  const themes = collectThemes(notes);
  const topTheme = themes[0]?.label ?? "signal priorities";
  const topAction = actionCounts.act > 0 ? "act" : actionCounts.prepare > 0 ? "prepare" : "monitor";

  return {
    title: `Top signals shaping ${topTheme} this week`,
    dateLabel: formatDateLabel(now),
    summary: notes.length
      ? `${notes.length} saved signal${notes.length === 1 ? "" : "s"} are informing this week's briefing posture, with ${actionCounts.act} immediate action signal${actionCounts.act === 1 ? "" : "s"} and ${actionCounts.prepare} items that need preparation.`
      : "No signals captured yet. Add a few notes to start building a briefing rhythm.",
    openingLine: notes.length
      ? `${notes.length} saved signals point toward a tighter ${topTheme} story for the week ahead.`
      : "Your signal inbox is ready for the first capture.",
    topSignals,
    watchlist,
    themes,
    actionCounts,
    signalCounts,
    bullets: buildBriefBullets(topSignals, topAction),
  };
}

export function createBriefRunRecord(brief: WeeklyBrief, now = new Date()): BriefRunRecord {
  const createdAt = now.toISOString();
  return {
    id: `brief-${now.getTime()}`,
    title: brief.title,
    dateLabel: brief.dateLabel,
    summary: brief.summary,
    createdAt,
    brief,
  };
}

export function draftNoteSuggestion(input: NoteDraftInput): NoteDraftSuggestion {
  const selectedText = normalizeText(input.selectedText ?? "");
  const title = input.title?.trim() || deriveTitle(selectedText) || "Captured signal";
  const category = input.category?.trim() || inferCategory(selectedText);
  const summary = summarize(selectedText || title);
  const riskType = inferRiskType(selectedText || `${title} ${category}`);
  const signalStrength = inferSignalStrength(selectedText || title);
  const suggestedAction = inferSuggestedAction(signalStrength, riskType);
  const tags = inferTags(`${title} ${selectedText} ${category}`);

  return {
    title,
    summary,
    signalStrength,
    riskType,
    category,
    suggestedAction,
    tags,
    slide: {
      headline: buildHeadline(title, summary, suggestedAction),
      bullets: [
        summary || "Signal captured for review.",
        `Recommended posture: ${suggestedAction}.`,
        `Primary risk lens: ${riskType}.`,
      ],
      speakerNote:
        "Treat this as a first-pass AI draft before finalizing the note for distribution.",
    },
  };
}

function normalizeText(value: string): string {
  return value.replaceAll(/\s+/g, " ").trim();
}

function summarize(value: string): string {
  if (!value) {
    return "";
  }
  const firstSentence = value.split(/(?<=[.!?])\s+/)[0] ?? value;
  return firstSentence.length > 220 ? `${firstSentence.slice(0, 217)}...` : firstSentence;
}

function deriveTitle(value: string): string {
  if (!value) {
    return "";
  }
  return (
    value
      .split(/(?<=[.!?])\s+/)[0]
      ?.slice(0, 72)
      .trim() ?? ""
  );
}

function inferCategory(value: string): string {
  const input = value.toLowerCase();
  if (input.includes("governance") || input.includes("policy") || input.includes("regulat")) {
    return "AI governance";
  }
  if (input.includes("vendor") || input.includes("procurement") || input.includes("third-party")) {
    return "third-party risk";
  }
  if (input.includes("board") || input.includes("executive") || input.includes("brief")) {
    return "executive communication";
  }
  if (input.includes("security") || input.includes("breach") || input.includes("incident")) {
    return "security posture";
  }
  return "signal review";
}

function inferRiskType(value: string): RiskType {
  const input = value.toLowerCase();
  if (input.includes("regulat") || input.includes("compliance") || input.includes("policy")) {
    return "regulatory";
  }
  if (input.includes("vendor") || input.includes("provider") || input.includes("third-party")) {
    return "vendor";
  }
  if (input.includes("security") || input.includes("breach") || input.includes("attack")) {
    return "security";
  }
  if (input.includes("geopolit") || input.includes("country") || input.includes("sanction")) {
    return "geopolitical";
  }
  if (input.includes("brand") || input.includes("reputation") || input.includes("trust")) {
    return "reputational";
  }
  return "operational";
}

function inferSignalStrength(value: string): SignalStrength {
  const count = value.split(/\s+/).filter(Boolean).length;
  if (count > 90) {
    return "high";
  }
  if (count > 35) {
    return "medium";
  }
  return "low";
}

function inferSuggestedAction(signalStrength: SignalStrength, riskType: RiskType): SuggestedAction {
  if (signalStrength === "high" || riskType === "security") {
    return "act";
  }
  if (riskType === "regulatory" || riskType === "vendor") {
    return "prepare";
  }
  return "monitor";
}

function inferTags(value: string): string[] {
  const input = value.toLowerCase();
  const tags = new Set<string>();
  if (input.includes("vendor")) {
    tags.add("vendor");
  }
  if (input.includes("board") || input.includes("executive")) {
    tags.add("briefing");
  }
  if (input.includes("risk")) {
    tags.add("risk");
  }
  if (input.includes("policy") || input.includes("regulat")) {
    tags.add("compliance");
  }
  if (input.includes("security")) {
    tags.add("security");
  }
  if (input.includes("procurement")) {
    tags.add("procurement");
  }
  return [...tags];
}

function buildHeadline(title: string, summary: string, action: SuggestedAction): string {
  if (summary) {
    return `${summary.slice(0, 80)}${summary.length > 80 ? "..." : ""}`;
  }
  return `${title} ${action === "act" ? "needs action now." : "needs review."}`;
}

function scoreNote(note: PithyNote): number {
  return (
    SIGNAL_RANK[note.signalStrength] * 3 +
    ACTION_RANK[note.suggestedAction] * 2 +
    (note.tags?.length ?? 0)
  );
}

function countByAction(notes: PithyNote[]): Record<SuggestedAction, number> {
  return notes.reduce<Record<SuggestedAction, number>>(
    (counts, note) => {
      counts[note.suggestedAction] += 1;
      return counts;
    },
    { monitor: 0, prepare: 0, act: 0 },
  );
}

function countByStrength(notes: PithyNote[]): Record<SignalStrength, number> {
  return notes.reduce<Record<SignalStrength, number>>(
    (counts, note) => {
      counts[note.signalStrength] += 1;
      return counts;
    },
    { low: 0, medium: 0, high: 0 },
  );
}

function collectThemes(notes: PithyNote[]): Array<{ label: string; count: number }> {
  const counts = new Map<string, number>();
  for (const note of notes) {
    const key = note.category?.trim() || note.riskType;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 4);
}

function buildBriefBullets(topSignals: PithyNote[], topAction: SuggestedAction): string[] {
  if (!topSignals.length) {
    return [
      "Capture a few signals from articles, meetings, or vendor notes.",
      "Use the AI draft action to convert raw text into a structured note.",
      "Return here to generate a weekly executive brief.",
    ];
  }

  const bullets = topSignals
    .slice(0, 3)
    .map((note) => note.slide.headline || note.summary || note.title);
  bullets.push(`Overall posture for the week: ${topAction}.`);
  return bullets;
}

function formatDateLabel(now: Date): string {
  return now.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
